import * as React from 'react'
import { useTranslation } from 'react-i18next'
import type { StatusMode } from '@/hooks/useProjectStatusMode'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { PROGRESS_TYPES } from '@/lib/types/subscription'
import { StageGlyph } from '@/components/app/indicators/StageGlyph'
import { stageForProgressType } from '@/lib/utils/status-stage'
import {
  PICKER_SIDE_OFFSET,
  PickerPanel,
  pickerPopoverClassName,
} from '@/components/app/combo-box/PickerPanel'
import { useStatusPickerOptions } from '@/routes/features/tasks/hooks/useStatusPickerOptions'
import { buildStageMap } from '@/routes/features/tasks/hooks/useStatusStage'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'

interface TaskStatusCheckboxProps {
  currentStatusId?: string | null
  onStatusChange: (statusId: string) => void
  statusMode?: StatusMode
  className?: string
}

/**
 * TaskStatusCheckbox - Inline circular status toggle for task lists, cards, and sub-tasks.
 *
 * - Simple mode: 2-state checkbox (Backlog ↔ Completed), no popover.
 * - Advanced mode: stage-glyph trigger that opens the shared status picker.
 *
 * For a searchable status dropdown with keyboard shortcuts (e.g., in task detail panels),
 * use StatusComboBox instead.
 */
export function TaskStatusCheckbox({
  currentStatusId,
  onStatusChange,
  statusMode = 'simple',
  className,
}: TaskStatusCheckboxProps) {
  const statusHook = useStatuses()

  const backlogStatus = React.useMemo(
    () =>
      statusHook.all.statuses.find(
        (s: any) => s.progressType === PROGRESS_TYPES.BACKLOG,
      ),
    [statusHook],
  )
  const completedStatus = React.useMemo(
    () =>
      statusHook.all.statuses.find(
        (s: any) => s.progressType === PROGRESS_TYPES.COMPLETED,
      ),
    [statusHook],
  )

  const currentStatus = React.useMemo(
    () => statusHook.all.statuses.find((s: any) => s.id === currentStatusId),
    [statusHook, currentStatusId],
  )

  const isCompleted =
    currentStatusId === completedStatus?.id ||
    currentStatus?.progressType === PROGRESS_TYPES.COMPLETED

  const isStatusReady = !!completedStatus && !!backlogStatus

  const [popoverOpen, setPopoverOpen] = React.useState(false)
  const { t } = useTranslation('common')
  // Same rows as the sidebar's StatusComboBox (design 31a), digits 1–9 to jump.
  const { options: statusOptions, action: pickerAction } = useStatusPickerOptions(
    statusHook.all.statuses,
    currentStatusId,
  )
  const pickerOptions = React.useMemo(
    () =>
      statusOptions.map((option, index) => ({
        ...option,
        shortcut: index < 9 ? String(index + 1) : undefined,
      })),
    [statusOptions],
  )
  const { isPersonal } = useWorkspaceMode()

  // Team workspaces: the stage glyph is the one status shape (design 31a), even
  // on simple-mode projects, where a click still just toggles done. The hollow
  // checkbox is kept for Personal mode.
  const stage =
    (currentStatus && buildStageMap(statusHook.all.statuses).get(currentStatus.id)) ??
    stageForProgressType(currentStatus?.progressType)
  const glyphButtonClass = cn(
    'flex items-center justify-center rounded-full transition-colors hover:bg-secondary',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
    className,
  )
  const glyph = <StageGlyph stage={stage} className="size-[72%]" />

  if (statusMode !== 'advanced' && !isPersonal) {
    const toggle = () => {
      const newStatusId = isCompleted ? backlogStatus?.id : completedStatus?.id
      if (newStatusId) onStatusChange(newStatusId)
    }
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={isCompleted}
        data-testid="task-status-checkbox"
        disabled={!isStatusReady}
        onClick={(e) => {
          e.stopPropagation()
          toggle()
        }}
        aria-label={!isStatusReady ? 'Loading...' : isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
        className={glyphButtonClass}
      >
        {glyph}
      </button>
    )
  }

  if (statusMode !== 'advanced') {
    return (
      <Checkbox
        data-testid="task-status-checkbox"
        checked={isCompleted}
        disabled={!isStatusReady}
        onCheckedChange={(checked: boolean) => {
          const newStatusId = checked
            ? completedStatus?.id
            : backlogStatus?.id
          if (newStatusId) {
            onStatusChange(newStatusId)
          }
        }}
        className={cn('rounded-full', className)}
        aria-label={
          !isStatusReady
            ? 'Loading...'
            : isCompleted
              ? 'Mark as incomplete'
              : 'Mark as complete'
        }
      />
    )
  }

  const triggerLabel = currentStatus?.name ?? 'Set status'

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="task-status-checkbox"
          disabled={!isStatusReady}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Change status: ${triggerLabel}`}
          aria-haspopup="listbox"
          className={glyphButtonClass}
        >
          {glyph}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={pickerPopoverClassName}
        align="start"
        sideOffset={PICKER_SIDE_OFFSET}
        onClick={(e) => e.stopPropagation()}
      >
        <PickerPanel
          options={pickerOptions}
          selected={currentStatusId ?? undefined}
          onSelect={(statusId) => {
            onStatusChange(statusId)
            setPopoverOpen(false)
          }}
          searchPlaceholder={t('picker.status.search')}
          action={
            pickerAction
              ? {
                  ...pickerAction,
                  onSelect: () => {
                    setPopoverOpen(false)
                    pickerAction.onSelect()
                  },
                }
              : undefined
          }
        />
      </PopoverContent>
    </Popover>
  )
}
